/**
 * The hydration ledger — which trades are COMPLETE, and therefore safe to save.
 *
 * ## The hazard this closes
 *
 * `tradeToDb` writes all 48 mapped columns on every save, and `tradeFromDb`
 * cannot tell an absent column from a SQL NULL — it turns both into a plausible
 * `0`, `''`, `false` or `[]`. Today nothing exploits that, because the loader
 * does `select('*')`. The moment a narrow projection lands for performance,
 * ONE save writes those manufactured defaults over the real values, for every
 * narrowly-loaded row, silently, while the UI reports success.
 *
 * The columns at risk are the whole journal: notes, screenshots, recordings,
 * mistake tags, confluences, the discipline booleans, MAE/MFE, pattern and
 * exit-model links, grade, and `extras` — which carries custom tags, the
 * import-batch stamp that Undo Import depends on, and `mergedFrom`, the only
 * surviving copy of merged-away trades. No database constraint catches it:
 * every at-risk column is nullable or defaulted, so the destructive write
 * commits cleanly, and `annotated` / `fees_auto` default TRUE, so they invert.
 *
 * ## Why an id, and not a key check
 *
 * The obvious guard is "refuse a trade with missing keys". It does not work,
 * and the way it fails is the ordinary way this code will be written:
 *
 *     const draft = { ...BLANK_TRADE, ...listRow };   // all 48 keys present
 *
 * That is how anyone builds an edit form from a list row, and a key-presence
 * check passes it while 37 of those keys are defaults about to be persisted.
 * TypeScript does not help either — it applies no excess-property checking to
 * spreads, so a brand on the projected type is no obstacle.
 *
 * An **id** survives spread, `Object.assign`, `structuredClone` and a JSON
 * round-trip, and it cannot be forged by a defaults object. So membership is
 * keyed on the id, and there are exactly two ways in:
 *
 *   - `markHydrated(id)`  — this row was read with every column present.
 *   - `markNew(id)`       — this trade was constructed locally and has no cloud
 *                           row yet, so there is nothing to destroy.
 *
 * Both are deliberate one-line acts at a boundary, in the same spirit as the
 * `asTradeId()` casts this codebase already confines to `io/`.
 *
 * ## Why it is passed, not owned
 *
 * A module-level `Set` would be less code and it is what most codebases do.
 * D42 settled the opposite for `selectors/` — state is an argument, never a
 * module global — and the reason applies verbatim here: a ledger that is
 * reachable by import is a ledger that a test cannot isolate, that two tabs
 * would share, and that nothing can reason about locally. `guard.ts` already
 * threads its baseline maps explicitly rather than owning them; this matches.
 */

/**
 * ⚠⚠ THE ID LEDGER IS NOT SUFFICIENT ON ITS OWN. Read this before trusting it.
 *
 * The reasoning that produced it — "an id cannot be forged by a defaults
 * spread" — is true and **irrelevant**, and an adversarial pass caught it after
 * the guard shipped. A projected row **carries the row's REAL id** (`id` is the
 * first member of `METRICS_KEYS`). Nothing has to be forged. So:
 *
 *   1. the user opens trade t1  → `markHydrated('t1')`
 *   2. a later list refresh re-projects t1 into a narrow row
 *   3. `{ ...BLANK_TRADE, ...narrowRow }` carries id t1
 *   4. `tradeToDb` → **the ledger says yes**, and the journal is blanked
 *
 * Reproduced, not theorised: notes, screenshots, mistake tags, booleans, MAE/MFE
 * and `extras` all written as defaults, with `annotated`/`fees_auto` inverted.
 *
 * Membership in an id set proves *"this id was fully read at some point"*. It
 * cannot express *"this object is the one that was read"*. So there is a second,
 * independent guard — see `PROJECTION_TAINT` below — and **both are required**:
 *
 *   ledger  → catches a trade that was never loaded at all
 *   taint   → catches a complete-looking object assembled from a projection
 */

/**
 * The key a projected row carries so that any object built from it is
 * recognisable as incomplete.
 *
 * ENUMERABLE and OWN, deliberately — the opposite of the usual instinct — because
 * the property that matters is that it **travels with the data**. Object spread,
 * `Object.assign`, `structuredClone` and a JSON round-trip all copy it, so every
 * way of laundering a narrow row into a trade-shaped object brings the marker
 * along. A non-enumerable marker or a symbol would be *dropped* by exactly the
 * operations being defended against, which is backwards.
 *
 * It is checked and rejected before `extractExtras` runs, so it can never be
 * persisted as data.
 */
export const PROJECTION_TAINT = '__tcProjection';

/** Was this object built from, or spread over, a column projection? */
export function isProjectionTainted(o: unknown): boolean {
  return !!o && typeof o === 'object' && PROJECTION_TAINT in (o as Record<string, unknown>);
}

/**
 * A record of which trade ids are known-complete.
 *
 * Deliberately NOT a bare `Set<string>`: the two ways in have different
 * meanings and different call sites, and naming them is what makes a review of
 * "who is allowed to save this?" possible at all.
 */
export interface HydrationLedger {
  /** Was this trade read with every column, or created here? */
  isComplete(id: string): boolean;
  /** This row came back from a `select('*')` — every column is present. */
  markHydrated(id: string): void;
  /** This trade was built locally and has no cloud row to overwrite. */
  markNew(id: string): void;
  /** Deleted, or evicted from memory. Saving it again requires a fresh read. */
  forget(id: string): void;
  /** For assertions and diagnostics. */
  readonly size: number;
}

export function createHydrationLedger(
  hydrated: Iterable<string> = [],
): HydrationLedger {
  const ids = new Set<string>(hydrated);
  return {
    isComplete: (id) => ids.has(id),
    markHydrated: (id) => void ids.add(id),
    markNew: (id) => void ids.add(id),
    forget: (id) => void ids.delete(id),
    get size() {
      return ids.size;
    },
  };
}

/**
 * A ledger that trusts everything.
 *
 * ⚠ ONLY for seeding a test database, where every trade is constructed in the
 * test itself and there is no pre-existing row to destroy. The `reason` is
 * required and unused — it exists so that every call site has to state, in
 * source, why bypassing the guard is safe there. A bypass nobody has to justify
 * becomes the default within a month.
 *
 * There is no source-scan forbidding this in production code, deliberately: a
 * grep guard is the exact thing D21 says not to trust. What stops it is that
 * the only honest `reason` for a production call site does not exist, and the
 * function is named so that reviewing one is unavoidable.
 */
export function trustEveryTrade(reason: string): HydrationLedger {
  if (!reason.trim()) throw new Error('trustEveryTrade: state why the bypass is safe here');
  return {
    isComplete: () => true,
    markHydrated: () => {},
    markNew: () => {},
    forget: () => {},
    get size() {
      return Infinity;
    },
  };
}
